// --- NEW FILE: packages/agent-kit/src/hooks/admin/useVersionHistory.ts ---

import { useProjectVersionHistory } from './projects/useProjectVersions';
import { useSprintVersionHistory } from './sprints/useSprintVersions';
import { useTaskVersionHistory } from './tasks/useTaskVersions';
import type { ProjectVersion, SprintVersion, TaskChangeLog, UUID } from '@dopomogai/types';
import type { AgentKitError } from '../types';

// Define the possible entity types and their corresponding data shapes
type HistoryEntityType = 'project' | 'sprint' | 'task';
type HistoryDataMap = {
  project: ProjectVersion[];
  sprint: SprintVersion[];
  task: TaskChangeLog[];
};

// Define a clear return type for the hook
interface UseVersionHistoryReturn<T extends HistoryEntityType> {
  data: HistoryDataMap[T] | undefined;
  isLoading: boolean;
  error: AgentKitError | null;
}

/**
 * A composite hook that acts as a single entry point for fetching the version
 * history of different entities. It simplifies UI components that need to display
 * version history for various items.
 *
 * @param entityType The type of entity ('project', 'sprint', or 'task').
 * @param entityId The ID of the entity.
 * @returns An object with the version data, loading state, and error state.
 */
export const useVersionHistory = <T extends HistoryEntityType>(
  entityType?: T, 
  entityId?: string | UUID
): UseVersionHistoryReturn<T> => {
  // Call all underlying hooks unconditionally, but pass undefined if the entity doesn't match.
  // This respects the Rules of Hooks.
  const { versions: projectVersions, isLoading: pLoading, error: pError } = 
    useProjectVersionHistory(entityType === 'project' ? entityId as UUID : undefined);
    
  const { versions: sprintVersions, isLoading: sLoading, error: sError } = 
    useSprintVersionHistory(entityType === 'sprint' ? entityId as UUID : undefined);
    
  const { history: taskHistory, isLoading: tLoading, error: tError } = 
    useTaskVersionHistory(entityType === 'task' ? entityId as string : undefined);

  // Return the data and state for the selected entity type.
  if (entityType === 'project') {
    return { data: projectVersions, isLoading: pLoading, error: pError } as UseVersionHistoryReturn<T>;
  }
  if (entityType === 'sprint') {
    return { data: sprintVersions, isLoading: sLoading, error: sError } as UseVersionHistoryReturn<T>;
  }
  if (entityType === 'task') {
    return { data: taskHistory, isLoading: tLoading, error: tError } as UseVersionHistoryReturn<T>;
  }
  
  // Default return when no entity is selected.
  return { data: undefined, isLoading: false, error: null };
};